/**
 * useInstrumentProfiles Hook
 * Fetches all instrument forge profiles from the API with fallback to static data.
 * Returns { data, loading, error, refetch } where data is Record<string, InstrumentForgeProfile>.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { InstrumentForgeProfile } from '@/features/InstrumentForge/types/instrumentForge.types';
import { API_ENDPOINTS } from '@/config/api';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';
import { normalizeInstrumentProfiles } from '@/features/InstrumentForge/utils/normalizeProfile';

interface UseInstrumentProfilesReturn {
  data: Record<string, InstrumentForgeProfile>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useInstrumentProfiles(): UseInstrumentProfilesReturn {
  const [data, setData] = useState<Record<string, InstrumentForgeProfile>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithCsrf(`${API_ENDPOINTS.instrumentPacks.list}?full=true`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.data && Object.keys(json.data).length > 0) {
        // AUTO-102: generator-made profiles are stored in a simpler shape; bring them to the full one on load.
        setData(normalizeInstrumentProfiles(json.data as Record<string, InstrumentForgeProfile>));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch instrument profiles';
      setError(msg);
      console.error('[useInstrumentProfiles] Fetch failed, loading static fallback chunk:', msg);
      try {
        const fallback = await import('@/features/InstrumentForge/data/instrumentForgeProfiles');
        setData(fallback.INSTRUMENTFORGE_PROFILES);
      } catch (fallbackErr) {
        console.error('[useInstrumentProfiles] Static fallback failed to load:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      refetch();
    }
  }, [refetch]);

  return { data, loading, error, refetch };
}
